<script setup>
// Page 2 — the month a manager recognises, before Pandig describes itself. The three
// rows are today's three opening paragraphs re-cut into the moments they name; nothing
// is added.
//
// Scroll-scrubbed over a taller sticky section, the shape LandingProblem set: the
// heading and the lead are never hidden, and the rows resolve from DIM rather than
// from nothing — a fast fling can come to rest anywhere in the range, so the argument
// has to read wherever you land. What the scrub buys is the sequence.
//
// One ink frame with hairline rows, not three icon cards — the same move LandingProblem
// makes on the homepage, and for the same reason: these are entries in a register of a
// month, the frame carries the panel's single hard shadow (One Hero Rule), and an icon
// beside "een mislukte incasso" illustrates nothing the sentence doesn't already say.
import { ref, onMounted, onUnmounted } from "vue";
import { gsap, ScrollTrigger, useMotion, reducedMotion, EASE, SCRUB, DIM } from "@/lib/motion";

const cards = [
  {
    title: "Dezelfde run, elke maand opnieuw",
    body: "Dezelfde run, dezelfde vragen, elke maand of elk kwartaal opnieuw — in een andere map, op een andere bankrekening.",
  },
  {
    title: "En dezelfde vragen erbij",
    body: "Staat elke eenheid klaar? Heeft elke actieve incasso een geldige machtiging? Wat is er binnengekomen, en wat is er misgegaan? Daarbij komen de vragen van eigenaren en het beeld dat het bestuur voor de vergadering wil.",
  },
  {
    title: "Het werk zit in de uitzonderingen",
    body: "Een ontbrekende machtiging, een mislukte incasso, een eenheid zonder eigenaar — en per VvE opnieuw hetzelfde overzicht opbouwen.",
  },
];

const root = ref(null);
const cardEls = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    gsap.set(cardEls.value, { opacity: DIM, y: 18 });

    const tl = gsap.timeline({ paused: true }).to(cardEls.value, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: EASE,
      stagger: 0.35,
    });

    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      animation: tl,
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- Its rows resolve across the section's own scroll range, so a nav/pager jump
       lands at the far end of that range with them arrived, not on greyed-out copy
       (data-jump="settled", see lib/scroll.js). -->
  <section
    id="uitzonderingen"
    ref="root"
    class="relative border-t-2 border-foreground bg-card"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[125vh] sm:h-[150vh]'"
    data-jump="settled"
    data-page-label="Het werk"
  >
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden'">
      <div class="mx-auto w-full max-w-6xl px-5 py-5 sm:py-14">
        <h2 class="max-w-3xl font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
          Het werk zit niet in de gewone maand.
        </h2>
        <p class="mt-2 max-w-3xl text-xs leading-snug text-muted-foreground sm:mt-4 sm:text-base sm:leading-relaxed">
          Voor één VvE is de inning nog te overzien met een bankafschrift en een lijstje. Bij tien of veertig VvE's verandert niet de moeilijkheid maar
          de frequentie.
        </p>

        <ol class="mt-4 border-2 border-foreground bg-background shadow-[var(--shadow-hard)] sm:mt-9">
          <li
            v-for="(card, i) in cards"
            :key="card.title"
            :ref="(el) => (cardEls[i] = el)"
            class="border-t-2 border-foreground/10 p-3 first:border-t-0 sm:grid sm:grid-cols-[16rem_1fr] sm:gap-6 sm:p-6"
          >
            <h3 class="font-sans text-sm font-semibold leading-snug tracking-tight text-foreground sm:text-base">{{ card.title }}</h3>
            <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-0 sm:text-sm sm:leading-relaxed">{{ card.body }}</p>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>
